import { Badge, Box, Flex, Input, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyCell } from '../../../components/EmptyCell'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { TableSkeletonRows } from '../../../components/Loading'
import Paginator from '../../../components/paginator'
import PaymentDateModal from '../../../components/PaymentDateModal'
import { FormSelect } from '../../../components/Select'
import { StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatCurrency, formatDate, formatDocument, formatIsoDate, useAppToast } from '../../../helpers'
import { EventService } from '../../../services/Event/EventService'
import { SelectEvent } from '../../../services/Event/types'
import EventCommissionService from '../../../services/EventCommissions/EventCommissionService'
import { IEventCommissionBeneficiary, IEventCommissionSale } from '../../../services/EventCommissions/interfaces'
import { beneficiaryLabel, beneficiaryScope, COMMISSION_PAYMENT_STATUS, sellerDisplayName } from './commissionDisplay'
import CommissionPaymentUndoDialog from './CommissionPaymentUndoDialog'

interface IProps {
  isOpen: boolean
  onClose: () => void
  /** Linha da listagem cujas vendas o modal mostra. */
  seller: IEventCommissionBeneficiary
  /** Mês herdado da listagem, para o modal abrir sobre o mesmo período. */
  initialMonth: string
  /** Vem da página para não repetir o useHasRole aqui dentro. */
  canPay: boolean
  /** Sem isto a linha atrás do modal continua com o recorte de pagamento antigo. */
  onPaymentsChanged: () => void
}

// Sete linhas cabem na altura do modal sem empurrar a paginação para fora do campo de visão.
const PAGE_SIZE = 7

/** Mesma regra do backend: venda sem comissão configurada, ou de R$ 0,00, não se paga. */
const isPayable = (sale: IEventCommissionSale) => sale.commission_value !== null && sale.commission_value > 0

/**
 * Rótulo e cor do botão de pagar, que muda de natureza conforme a venda.
 *
 * Numa venda já quitada ele só corrige a data — é edição, não pagamento, então vai de verde
 * para o azul de editar do painel. Devolver um objeto (em vez de passar `colorScheme={undefined}`
 * no call site) é o que preserva a cor do registry nos outros casos: a prop presente com valor
 * undefined venceria o padrão do ActionButton e o botão cairia para cinza.
 */
function payActionProps(sale: IEventCommissionSale): { label?: string; colorScheme?: string } {
  if (!isPayable(sale)) return { label: 'Esta venda não gera comissão' }

  return sale.paid_on ? { label: 'Corrigir data do pagamento', colorScheme: 'blue' } : {}
}

export default function SellerSalesModal({ isOpen, onClose, seller, initialMonth, canPay, onPaymentsChanged }: IProps) {
  const toast = useAppToast()
  const service = useMemo(() => new EventCommissionService(), [])
  const eventService = useMemo(() => new EventService(), [])

  const [sales, setSales] = useState<IEventCommissionSale[]>([])
  const [events, setEvents] = useState<SelectEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({ search: '', event_id: '' as number | '', month: initialMonth })
  const [payingSale, setPayingSale] = useState<IEventCommissionSale | null>(null)
  const [undoingSale, setUndoingSale] = useState<IEventCommissionSale | null>(null)
  const [savingUuid, setSavingUuid] = useState<string | null>(null)
  const columnCount = canPay ? 8 : 7

  useEffect(() => {
    eventService
      .listForSelect()
      .then(setEvents)
      .catch(() => setEvents([]))
  }, [eventService])

  const fetchSales = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.listSales({
        ...filters,
        ...beneficiaryScope(seller),
        page,
        page_size: PAGE_SIZE
      })
      setSales(data.data)
      setTotal(data.total)
      setTotalPages(Math.ceil(data.total / PAGE_SIZE) || 1)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as vendas', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    const delayDebounce = setTimeout(() => fetchSales(), 400)

    return () => clearTimeout(delayDebounce)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters, page])

  /** A listagem de trás soma estas vendas, então as duas telas recarregam juntas. */
  const afterPaymentChange = () => {
    fetchSales()
    onPaymentsChanged()
  }

  const confirmSalePayment = async (date: string) => {
    if (!payingSale) return

    setSavingUuid(payingSale.order_uuid)
    try {
      await service.markSalePaid(payingSale.order_uuid, date)
      toast.success('Pagamento registrado')
      setPayingSale(null)
      afterPaymentChange()
    } catch (error) {
      toast.error('Erro ao registrar o pagamento', error)
    } finally {
      setSavingUuid(null)
    }
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <>
            <Text>Vendas de {sellerDisplayName(seller)}</Text>
            <Text variant="caption">{beneficiaryLabel(seller)}</Text>
          </>
        }
        size="editor"
        /* Oito colunas mais filtros e paginação não cabem na largura padrão do `editor`. O min() no
           minH é necessário porque, em janelas baixas, o min-height venceria o maxH que o
           scrollBehavior="inside" aplica e o modal estouraria a viewport. */
        contentProps={{ maxW: '90vw', minH: 'min(70vh, 100%)' }}
        /* A tabela cresce e encolhe no meio; filtros e paginação ficam fixos nas pontas. */
        isBodyColumn
      >
        <Flex gap={3} flexWrap="wrap" align="flex-end" mb={4} flexShrink={0}>
          <FormField label="Evento" maxW="280px">
            <FormSelect
              size="sm"
              value={filters.event_id}
              onChange={(value) => {
                setFilters((f) => ({ ...f, event_id: value === '' ? '' : Number(value) }))
                setPage(1)
              }}
              options={events.map((event) => ({ value: event.id, label: event.name }))}
              placeholder="Todos os eventos"
            />
          </FormField>

          <FormField label="Mês da compra" maxW="180px">
            <Input
              type="month"
              size="sm"
              value={filters.month}
              onChange={(e) => {
                setFilters((f) => ({ ...f, month: e.target.value }))
                setPage(1)
              }}
            />
          </FormField>

          <FormField label="Buscar" maxW="260px">
            <Input
              type="search"
              size="sm"
              placeholder="Nome ou CPF do comprador"
              value={filters.search}
              onChange={(e) => {
                setFilters((f) => ({ ...f, search: e.target.value }))
                setPage(1)
              }}
            />
          </FormField>
        </Flex>

        {/* overflowY="auto" derruba o `overflowY: hidden` embutido do TableContainer, que recortava
          as linhas: por ser scroll container ele é o único filho flex que encolhe abaixo do
          próprio conteúdo, e sozinho absorvia toda a falta de altura do modal. flex="1" faz a
          tabela ocupar a sobra vertical; minH impede que ela colapse em janelas muito baixas. */}
        <TableContainer flex="1" minH="140px" overflowY="auto">
          <Table>
            {/* Cabeçalho grudado no topo enquanto as linhas rolam. O sticky vai nos th, e não no
              thead, porque a tabela é border-collapse: no thead a borda inferior fica para trás
              ao rolar — daí também o inset shadow no lugar dela. O bg é obrigatório: o th do
              Chakra é transparente e as linhas passariam por baixo. */}
            <Thead
              sx={{
                th: {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  bg: 'white',
                  boxShadow: 'inset 0 -1px 0 var(--chakra-colors-gray-100)'
                }
              }}
            >
              <Tr>
                <Th>Nome do comprador</Th>
                <Th>Cupom usado</Th>
                <Th isNumeric>Valor da Venda</Th>
                <Th>Nome do Evento</Th>
                <Th>Data da compra</Th>
                <Th isNumeric>Valor da comissão</Th>
                <Th>Pagamento</Th>
                {canPay && <Th>Ações</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <TableSkeletonRows cols={columnCount} rows={PAGE_SIZE} />
              ) : loadError ? (
                <TableErrorRow colSpan={columnCount} entity="vendas" feminine onRetry={fetchSales} />
              ) : sales.length === 0 ? (
                <TableEmptyRow colSpan={columnCount} entity="venda" feminine />
              ) : (
                sales.map((sale) => (
                  <Tr key={sale.order_uuid}>
                    <Td>
                      <Text fontWeight="500" color="gray.800">
                        <EmptyCell value={sale.buyer_name} />
                      </Text>
                      <EmptyCell variant="caption" value={sale.buyer_cpf && formatDocument(sale.buyer_cpf, true)} />
                    </Td>
                    <Td>{sale.coupon_code ? <Badge colorScheme="purple">{sale.coupon_code}</Badge> : '-'}</Td>
                    <Td isNumeric>{formatCurrency(sale.sale_value)}</Td>
                    {/* Event names are long; wrapping keeps the commission column on screen. */}
                    <Td maxW="280px" whiteSpace="normal">
                      <EmptyCell color="gray.800" value={sale.event_name} />
                      {sale.event_sold_out && <Text variant="caption">Evento lotado</Text>}
                    </Td>
                    <Td>{formatDate(sale.purchased_at)}</Td>
                    <Td isNumeric>
                      {sale.commission_value === null ? (
                        <EmptyCell />
                      ) : (
                        <>
                          <Text color="gray.700">{formatCurrency(sale.commission_value)}</Text>
                          <Text variant="caption">{sale.commission_rate}%</Text>
                        </>
                      )}
                    </Td>
                    <Td>
                      <StatusBadge status={sale.paid_on ? 'paid' : 'pending'} statuses={COMMISSION_PAYMENT_STATUS} />
                      {sale.paid_on && (
                        <Text variant="caption" mt={1}>
                          {formatIsoDate(sale.paid_on)}
                          {sale.paid_by ? ` por ${sale.paid_by}` : ''}
                        </Text>
                      )}
                    </Td>
                    {canPay && (
                      <Td>
                        <ActionButton
                          action="mark-paid"
                          {...payActionProps(sale)}
                          isDisabled={!isPayable(sale) || savingUuid === sale.order_uuid}
                          onClick={() => setPayingSale(sale)}
                        />
                        {sale.paid_on && (
                          <ActionButton
                            action="unmark-paid"
                            isDisabled={savingUuid === sale.order_uuid}
                            onClick={() => setUndoingSale(sale)}
                          />
                        )}
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Sem encolher: quem segura a contagem e a paginação embaixo é o flex="1" da tabela —
          com uma venda só elas subiriam junto e deixariam um vão solto sob os botões. */}
        <Box pt={2} flexShrink={0}>
          {!loading && sales.length > 0 && (
            <Text variant="caption" mt={2}>
              {total} venda{total === 1 ? '' : 's'} encontrada{total === 1 ? '' : 's'}.
            </Text>
          )}

          <Paginator page={page} totalPages={totalPages} changePage={(p: number) => setPage(p)} />
        </Box>
      </FormModal>

      {/* Fora do <FormModal> de propósito: irmão dele, o gerenciador do Chakra dá o foco ao que
          abriu por último em vez de a caixa de data disputar com o trap do modal de fora. */}
      {payingSale && (
        <PaymentDateModal
          isOpen={!!payingSale}
          onClose={() => setPayingSale(null)}
          onConfirm={confirmSalePayment}
          defaultDate={payingSale.paid_on ?? undefined}
          isLoading={savingUuid === payingSale.order_uuid}
          closeOnConfirm={false}
          title={payingSale.paid_on ? 'Corrigir data do pagamento' : 'Registrar pagamento da venda'}
          description={`Comissão de ${formatCurrency(payingSale.commission_value ?? 0)} sobre a compra de ${payingSale.buyer_name ?? 'comprador não identificado'}. Escolha a data em que o pagamento foi realizado:`}
        />
      )}

      {/* Também irmão do <FormModal>, pelo mesmo motivo do seletor de data. */}
      {undoingSale && (
        <CommissionPaymentUndoDialog
          isOpen={!!undoingSale}
          onClose={() => setUndoingSale(null)}
          description={`A comissão de ${formatCurrency(undoingSale.commission_value ?? 0)} sobre a compra de ${undoingSale.buyer_name ?? 'comprador não identificado'} voltará para pendente. Tem certeza?`}
          onConfirm={() => service.unmarkSalePaid(undoingSale.order_uuid)}
          onUndone={afterPaymentChange}
        />
      )}
    </>
  )
}
